import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";

const AuthContext = createContext(null);
const STORAGE_KEY = "az_admin_token";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function AdminAuthProvider({ children }) {
  const [token, setToken] = useState(() => {
    try { return window.localStorage.getItem(STORAGE_KEY) || null; } catch { return null; }
  });
  const [email, setEmail] = useState(null);
  const [checking, setChecking] = useState(Boolean(token));

  useEffect(() => {
    let cancelled = false;
    async function verify() {
      if (!token) { setChecking(false); return; }
      try {
        const { data } = await axios.get(`${API}/admin/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setEmail(data.email);
      } catch {
        if (!cancelled) {
          setToken(null);
          try { window.localStorage.removeItem(STORAGE_KEY); } catch (e) { void e; }
          setEmail(null);
        }
      } finally {
        if (!cancelled) setChecking(false);
      }
    }
    verify();
    return () => { cancelled = true; };
  }, [token]);

  const login = useCallback(async (emailValue, password) => {
    const { data } = await axios.post(`${API}/admin/login`, { email: emailValue, password });
    try { window.localStorage.setItem(STORAGE_KEY, data.access_token); } catch (e) { void e; }
    setToken(data.access_token);
    setEmail(data.email);
    return data;
  }, []);

  const logout = useCallback(() => {
    try { window.localStorage.removeItem(STORAGE_KEY); } catch (e) { void e; }
    setToken(null);
    setEmail(null);
  }, []);

  const authFetch = useCallback((url, opts = {}) => {
    return axios({
      url: url.startsWith("http") ? url : `${API}${url}`,
      ...opts,
      headers: {
        ...(opts.headers || {}),
        Authorization: `Bearer ${token}`,
      },
    });
  }, [token]);

  const value = useMemo(() => ({
    token, email, checking, isAuthenticated: Boolean(token && email), login, logout, authFetch,
  }), [token, email, checking, login, logout, authFetch]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAdminAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAdminAuth must be used within AdminAuthProvider");
  return ctx;
}
